import React, { useEffect, useMemo, useState } from "react";
import { Box, Container, Grid, Tab, Tabs, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { useParams } from "react-router-dom";
import { formatParticipantReference } from "common/build/utils/participantReference";
import { Breadcrumb } from "../../core/components/Breadcrumb";
import Spinner from "../layout/Spinner";
import { Error } from "../../core/components/Error";
import { verbalTheme } from "../layout/themes/verbal.theme";
import { getOverview } from "../../client/api/participants";
import { ParticipantType, MoodType } from "../../models/modelTypes";
import { useAuth } from "../../contexts/auth.context";
import { useMobile } from "../../hooks/useMobile";
import { useQueryStringState } from "../../hooks/useQueryString";
import { ParticipantOverviewTab } from "./ParticipantOverviewTab";
import ParticipantImpactTab from "./ParticipantImpactTab";
import { ParticipantSummarySidebar } from "./ParticipantSummarySidebar";
import { ParticipantMoodSection } from "./ParticipantMoodSection";
import { aggregateSessions, countCompletedSessions, getProgrammeSummaryLabel } from "../../utils/participantOverview";

const TAB_KEYS = ["overview", "impact", "learning", "session-notes", "fidelity"] as const;
type TabKey = (typeof TAB_KEYS)[number];
const FUTURE_TABS = new Set<TabKey>(["learning", "session-notes", "fidelity"]);

const TAB_LABELS: Record<TabKey, string> = {
    overview: "Overview",
    impact: "Impact scores",
    learning: "Learning scores",
    "session-notes": "Session notes",
    fidelity: "Fidelity",
};

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps): JSX.Element {
    const { children, value, index } = props;

    return (
        <div role="tabpanel" hidden={value !== index} id={`participant-tabpanel-${index}`}>
            {value === index && <Box pt={3}>{children}</Box>}
        </div>
    );
}

export const ViewParticipant: React.FC = () => {
    const params = useParams();
    const participantId = parseInt(params.id ?? "", 10);
    const { user } = useAuth();
    const { isMobile, isTablet } = useMobile();
    const tabs = user?.isSchool ? TAB_KEYS.filter((key) => key !== "impact") : TAB_KEYS;
    const [tabValue, setTabValue] = useQueryStringState("tab", TAB_KEYS[0]);
    const tabIndex = Math.max(tabs.indexOf(tabValue as TabKey), 0);
    const [participant, setParticipant] = useState<ParticipantType | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [programmeIdFilter, setProgrammeIdFilter] = useState<number | undefined>();
    const fullScreen = useMediaQuery(verbalTheme.breakpoints.down("lg"));

    useEffect(() => {
        if (Number.isNaN(participantId)) {
            setError(true);
            setLoading(false);
            return;
        }

        setLoading(true);
        getOverview(participantId)
            .then((data) => {
                setParticipant(data);
                setError(false);
            })
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, [participantId]);

    const programmes = participant?.ParticipantToProgramme ?? [];
    const reference = formatParticipantReference(participantId);
    const allSessions = useMemo(() => aggregateSessions(programmes), [programmes]);
    const completedSessions = countCompletedSessions(allSessions);
    const summaryParts = useMemo(() => {
        if (programmes.length === 0) {
            return user?.isSchool
                ? [
                      participant?.ParticipantToClassroom?.map((ptc) => ptc.Classroom?.name)
                          .filter(Boolean)
                          .join(", ") || "Classroom student",
                  ]
                : [];
        }

        const uniqueLabels = [...new Set(programmes.map((ptp) => getProgrammeSummaryLabel(ptp)))];
        return [
            `${programmes.length} programme${programmes.length === 1 ? "" : "s"}`,
            ...uniqueLabels.slice(0, 2),
            allSessions.length > 0 ? `Session ${completedSessions} of ${allSessions.length}` : null,
        ].filter((value): value is string => value != null);
    }, [programmes, allSessions.length, completedSessions, participant?.ParticipantToClassroom, user?.isSchool]);

    const schoolMoods = useMemo(() => {
        if (!user?.isSchool) {
            return [] as MoodType[];
        }

        return (
            participant?.ParticipantToClassroom?.flatMap((ptc) => ptc.Moods ?? []).filter((mood) => mood.isPresent) ??
            []
        );
    }, [participant?.ParticipantToClassroom, user?.isSchool]);

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setTabValue(tabs[newValue]);
    };

    if (loading) {
        return <Spinner />;
    }

    if (error || !participant) {
        return <Error description="Unable to load participant" />;
    }

    return (
        <>
            {!isTablet && isMobile && <div style={{ marginTop: 56 }} />}
            <Box sx={{ bgcolor: "secondaryShade.main" }}>
                <Container maxWidth="lg">
                    <Box p={4} pb={0}>
                        <Box mb={2}>
                            <Breadcrumb
                                crumbs={[{ name: "My Participants", url: "/participants" }]}
                                current={reference}
                            />
                        </Box>
                        <Typography variant="h2" component="h1" gutterBottom>
                            {reference}
                        </Typography>
                        {user?.isSchool && participant.name !== reference && (
                            <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                                {participant.name}
                            </Typography>
                        )}
                        <Typography variant="body2" color="textSecondary">
                            {summaryParts.join(" · ")}
                        </Typography>
                    </Box>
                    <Tabs
                        value={tabIndex}
                        onChange={handleTabChange}
                        indicatorColor="secondary"
                        textColor="primary"
                        variant={fullScreen ? "scrollable" : "standard"}
                        scrollButtons={fullScreen ? "auto" : undefined}
                    >
                        {tabs.map((tabKey, index) => {
                            const label = TAB_LABELS[tabKey];

                            if (FUTURE_TABS.has(tabKey)) {
                                return (
                                    <Tab
                                        key={tabKey}
                                        label={
                                            <Tooltip title="Available in a future release">
                                                <span>{label}</span>
                                            </Tooltip>
                                        }
                                        disabled
                                        id={`participant-tab-${index}`}
                                    />
                                );
                            }

                            return <Tab key={tabKey} label={label} id={`participant-tab-${index}`} />;
                        })}
                    </Tabs>
                </Container>
            </Box>

            <Container maxWidth="lg">
                <Box p={4} pt={0}>
                    <TabPanel value={tabIndex} index={0}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={8}>
                                <ParticipantOverviewTab
                                    programmes={programmes}
                                    programmeIdFilter={programmeIdFilter}
                                    onProgrammeFilterChange={setProgrammeIdFilter}
                                />
                                {user?.isSchool && (
                                    <ParticipantMoodSection
                                        moods={schoolMoods}
                                        participantId={participantId}
                                        participantName={participant.name}
                                    />
                                )}
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <ParticipantSummarySidebar
                                    programmes={programmes}
                                    programmeIdFilter={programmeIdFilter}
                                />
                            </Grid>
                        </Grid>
                    </TabPanel>
                    {!user?.isSchool && (
                        <TabPanel value={tabIndex} index={tabs.indexOf("impact")}>
                            <ParticipantImpactTab participantId={participantId} />
                        </TabPanel>
                    )}
                </Box>
            </Container>
        </>
    );
};

export default ViewParticipant;
